import type { ReactNode } from "react";
import type { ColumnDef } from "@tanstack/react-table";
import CurrencyFormat from "react-currency-format";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSort } from "@fortawesome/pro-light-svg-icons";
import { faSortDown, faSortUp } from "@fortawesome/free-solid-svg-icons";
import type { ColumnSort, ProjectRow, UserViewColumn } from "../types";

export interface ColumnContext {
  statusLabel: (status: string) => string;
  projectHref: (projectId: string) => string;
  sort?: ColumnSort;
  onSort?: (column: UserViewColumn) => void;
}

// Keys the v2 projects sort union accepts (generated V2ListProjectsForAccountApiArg["sort"]).
export const SORTABLE_KEYS: ReadonlySet<string> = new Set([
  "project-name", "status", "submitted-at", "approved-at", "total-effort", "total-revenue",
  "ms-revenue", "third-party-revenue", "contract-revenue", "contract-cost", "contract-margin",
  "net-mrr", "created-at", "updated-at", "tag-list", "client.name", "presales-engineer.name",
  "sales-executive.name", "service-category", "price-adjustment", "mrr-adjustment", "mrr-terms",
]);

// The *-name attributes are calculated from a belongs-to, so the API sorts
// them through the relationship: `client.name`, not `client-name`.
const RELATIONSHIP_NAME_SORTS: Readonly<Record<string, string>> = {
  "client-name": "client.name",
  "sales-executive-name": "sales-executive.name",
  "presales-engineer-name": "presales-engineer.name",
};

// Mirrors scopestack-api config/project-columns.yml "projects" (selected flags included).
export const DEFAULT_COLUMNS: UserViewColumn[] = [
  { label: "Project Name", width: 3, format: "string", data_path: "attributes.project-name", data_type: "string", selected: true, optional: false, sortable: true, sorted: "false", position: 1 },
  { label: "Status", width: 1, format: "titleize", data_path: "attributes.status", data_type: "string", selected: true, optional: true, sortable: true, sorted: "false", position: 2 },
  { label: "Client", width: 2, format: "string", data_path: "relationships.client.name", data_type: "string", selected: true, optional: true, sortable: true, sorted: "false", position: 3 },
  { label: "Pre-Sales", width: 1, format: "string", data_path: "relationships.presales-engineer.name", data_type: "string", selected: true, optional: true, sortable: true, sorted: "false", position: 4 },
  { label: "Sales", width: 1, format: "string", data_path: "relationships.sales-executive.name", data_type: "string", selected: true, optional: true, sortable: true, sorted: "false", position: 5 },
  { label: "Last Edited On", width: 1, format: "date", data_path: "attributes.updated-at", data_type: "date", selected: true, optional: true, sortable: true, sorted: "false", position: 6 },
  { label: "Revenue", width: 1, format: "currency", data_path: "attributes.contract-revenue", data_type: "decimal", selected: true, optional: true, sortable: false, sorted: "false", position: 7 },
  { label: "Blended Margin", width: 1, format: "percent", data_path: "attributes.contract-margin", data_type: "decimal", selected: true, optional: true, sortable: false, sorted: "false", position: 8 },
  { label: "Created On", width: 1, format: "date", data_path: "attributes.created-at", data_type: "date", selected: true, optional: true, sortable: true, sorted: "desc", position: 9 },
  { label: "Submitted On", width: 1, format: "date", data_path: "attributes.submitted-at", data_type: "date", selected: false, optional: true, sortable: true, sorted: "false", position: 10 },
  { label: "Approved On", width: 1, format: "date", data_path: "attributes.approved-at", data_type: "date", selected: false, optional: true, sortable: true, sorted: "false", position: 11 },
  { label: "MRR", width: 1, format: "currency", data_path: "attributes.net-mrr", data_type: "decimal", selected: false, optional: true, sortable: false, sorted: "false", position: 12 },
  { label: "Cost", width: 1, format: "currency", data_path: "attributes.contract-cost", data_type: "decimal", selected: false, optional: true, sortable: false, sorted: "false", position: 13 },
  { label: "Tags", width: 1, format: "string", data_path: "attributes.tag-list", data_type: "string", selected: false, optional: true, sortable: false, sorted: "false", position: 14 },
  { label: "Service Category", width: 2, format: "string", data_path: "attributes.service-category", data_type: "string", selected: false, optional: true, sortable: false, sorted: "false", position: 15 },
];

const RELATIONSHIP_FIELDS = {
  client: "client",
  "sales-executive": "salesExecutive",
  "presales-engineer": "presalesEngineer",
} as const;

const isBlank = (v: unknown) => v === null || v === undefined || v === "";

export function resolveColumnValue(column: UserViewColumn, row: ProjectRow): unknown {
  const [root, ...rest] = column.data_path.split(".");
  if (root === "attributes") {
    const attrs = (row.project.attributes ?? {}) as Record<string, unknown>;
    if (rest[0] === "project-variables") {
      const vars = attrs["project-variables"];
      if (!Array.isArray(vars)) return undefined;
      const found = vars.find((v: { name?: string }) => v?.name === rest[1]);
      return rest[2] && found ? (found as Record<string, unknown>)[rest[2]] : found;
    }
    return attrs[rest[0]];
  }
  if (root === "relationships") {
    const field = RELATIONSHIP_FIELDS[rest[0] as keyof typeof RELATIONSHIP_FIELDS];
    if (!field) return undefined;
    return row[field]?.attributes?.[rest[1]];
  }
  return undefined;
}

const titleize = (s: string) =>
  s.replace(/[_-]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

export function formatColumnValue(
  column: UserViewColumn,
  value: unknown,
  row: ProjectRow,
  ctx: ColumnContext
): ReactNode {
  if (isBlank(value)) return "";
  if (Array.isArray(value)) return value.join(", ");
  if (column.data_path === "attributes.status") return ctx.statusLabel(String(value));
  if (column.data_type === "date" || column.format === "date") {
    const d = new Date(String(value));
    return Number.isNaN(d.getTime())
      ? ""
      : d.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
  }
  if (column.format === "percent") {
    const n = parseFloat(String(value));
    return Number.isNaN(n) ? "" : `${(100 * n).toFixed(2)}%`;
  }
  if (column.format === "currency") {
    const n = parseFloat(String(value));
    if (Number.isNaN(n)) return "";
    const unit = (row.project.attributes?.["field-labels"] as { currency_unit?: string } | undefined)?.currency_unit ?? "$";
    return (
      <CurrencyFormat displayType="text" prefix={unit} isNumericString thousandSeparator value={n.toFixed(2)} />
    );
  }
  if (column.format === "titleize") return titleize(String(value));
  return String(value);
}

export function columnSortKey(column: UserViewColumn): string | null {
  if (!column.sortable) return null;
  const [root, ...rest] = column.data_path.split(".");
  let key: string | null = null;
  if (root === "attributes" && rest.length === 1) key = rest[0];
  if (root === "relationships" && rest.length === 2 && rest[1] === "name") key = `${rest[0]}.name`;
  if (key && RELATIONSHIP_NAME_SORTS[key]) key = RELATIONSHIP_NAME_SORTS[key];
  return key && SORTABLE_KEYS.has(key) ? key : null;
}

export function sortFromColumns(columns: UserViewColumn[]): ColumnSort {
  const sorted = columns.find((c) => (c.sorted === "asc" || c.sorted === "desc") && columnSortKey(c));
  if (sorted) return { key: columnSortKey(sorted) as string, direction: sorted.sorted as "asc" | "desc" };
  return { key: "created-at", direction: "desc" };
}

export const sortParam = (sort: ColumnSort): string =>
  sort.direction === "desc" ? `-${sort.key}` : sort.key;

export function nextSortColumns(columns: UserViewColumn[], clicked: UserViewColumn): UserViewColumn[] {
  const effective = sortFromColumns(columns);
  const wasSorted = columnSortKey(clicked) === effective.key;
  return columns.map((c) => {
    if (c.data_path !== clicked.data_path) return { ...c, sorted: "false" };
    return { ...c, sorted: wasSorted && effective.direction === "desc" ? "asc" : "desc" };
  });
}

const NameCell = ({ row, ctx }: { row: ProjectRow; ctx: ColumnContext }) => {
  const name = String(row.project.attributes?.["project-name"] ?? "");
  const count = row.changeOrders.length;
  return (
    <span className="projectNameCell">
      {row.isChangeOrder && (
        <span className="changeOrderBadge">
          {row.changeOrderNumber != null ? `CR #${row.changeOrderNumber}` : "CR"}
        </span>
      )}
      <a href={ctx.projectHref(row.id)} className="projectNameLink">
        {name}
      </a>
      {count > 0 && <span className="changeOrderCountPill">{`${count} ${count === 1 ? "CR" : "CRs"}`}</span>}
    </span>
  );
};

const SortHeader = ({ column, ctx }: { column: UserViewColumn; ctx: ColumnContext }) => {
  const key = columnSortKey(column);
  if (!key || !ctx.onSort) return <>{column.label}</>;
  const active = ctx.sort?.key === key ? ctx.sort.direction : undefined;
  const name = active ? `${column.label}, sorted ${active === "asc" ? "ascending" : "descending"}` : `Sort by ${column.label}`;
  const icon = active === "asc" ? faSortUp : active === "desc" ? faSortDown : faSort;
  return (
    <button
      type="button"
      className="sortHeader"
      aria-label={name}
      onClick={() => ctx.onSort?.(column)}
    >
      {column.label} <FontAwesomeIcon icon={icon} />
    </button>
  );
};

// A saved width is in legacy grid units (the Customize Table limit is 12);
// views saved by older code may hold it as a string. Anything unusable is 1.
const widthUnits = (column: UserViewColumn): number => {
  const n = Number(column.width);
  return Number.isFinite(n) && n > 0 ? n : 1;
};

const percent = (n: number) => `${Number(n.toFixed(4))}%`;

export function buildColumns(columns: UserViewColumn[], ctx: ColumnContext): ColumnDef<ProjectRow>[] {
  const shown = columns.filter((c) => c.selected).sort((a, b) => a.position - b.position);
  // Each column's share of the selected total, so the table fills its width
  // (legacy used col-N classes, i.e. N/12 of the row).
  const total = shown.reduce((sum, c) => sum + widthUnits(c), 0);
  return shown.map((column) => ({
      id: column.label,
      meta: { width: percent((100 * widthUnits(column)) / total) },
      header: () => <SortHeader column={column} ctx={ctx} />,
      cell: ({ row }: { row: { original: ProjectRow } }) =>
        column.data_path === "attributes.project-name" ? (
          <NameCell row={row.original} ctx={ctx} />
        ) : (
          formatColumnValue(column, resolveColumnValue(column, row.original), row.original, ctx)
        ),
    }));
}
